/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SystemVarsCategoryTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the System Vars module.
----------------------------------------------------------------*/


import React from 'react';
import { Settings } from 'lucide-react';
import { TabsContent } from '../../../components/ui/tabs';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { SystemVar } from '../types/systemVars.types';
import { SystemVarsFieldWithReset } from './SystemVarsFieldWithReset';
import { SystemVarsFieldRenderer } from './SystemVarsFieldRenderer';

interface Props {
    category: string;
    settings: SystemVar[];
    formData: Record<string, string>;
    isModifiedFromDefault: (name: string) => boolean;
    onResetFieldToDefault: (name: string) => void;
    onFieldChange: (name: string, value: string) => void;
}

export function SystemVarsCategoryTab({ category, settings, formData, isModifiedFromDefault, onResetFieldToDefault, onFieldChange }: Props) {
    const { t } = useI18n();
    const sorted = [...settings].sort((a, b) => Number(a.settingMetadata.setting_order ?? 0) - Number(b.settingMetadata.setting_order ?? 0));

    return (
        <TabsContent key={category} value={category} className="mt-0">
            <FormBox title={t(`systemVars.categories.${category}`, category)} icon={<Settings className="h-5 w-5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    {sorted.map((setting) => {
                        const name = setting.settingMetadata.variable_name;
                        return (
                            <React.Fragment key={setting.id}>
                                <SimpleTooltip content={t(`systemVars.tooltips.${name}`, setting.description)} position="top">
                                    <Label className="text-sm font-medium">
                                        {t(`systemVars.fields.${name}`, setting.name)}
                                        {setting.settingMetadata.is_required && <span className="text-red-500 ml-1">*</span>}
                                    </Label>
                                </SimpleTooltip>
                                <SystemVarsFieldWithReset isModified={isModifiedFromDefault(name)} onResetToDefault={() => onResetFieldToDefault(name)}>
                                    <SystemVarsFieldRenderer setting={setting} currentValue={formData[name] || ''} onFieldChange={onFieldChange} />
                                </SystemVarsFieldWithReset>
                            </React.Fragment>
                        );
                    })}
                </div>
            </FormBox>
        </TabsContent>
    );
}
